import {type LineItem, type SubLineItem} from "../models/CartData";
import {userIsSalesman} from "../../utils/roles";
import * as QuoteUpdateApi from "../../QuoteCart/api/UpdateQuotePackageEntry";
import * as CartUpdateApi from "./UpdatePackageEntry";
import * as CartDeleteApi from "./DeletePackageEntry";
import * as QuoteAddApi from "../../QuoteCart/api/AddToQuote";
import * as CartAddApi from "./AddToCart";
import * as CartLoadApi from "./CartView";
import * as QuoteLoadApi from "../../QuoteCart/api/QuoteView";
import {Permission, userHasPermission} from "../../utils/permissions";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

export function sendDeleteEntry(entry: LineItem): Promise<unknown> {
  entry = { ...entry, quantity: 0 };
  return internalSendDeleteEntry(entry, []);
}

export function sendDeletePartEntry(parentLineItem: LineItem, subLineItemToDelete: SubLineItem): Promise<unknown> {
  subLineItemToDelete = { ...subLineItemToDelete, quantity: 0 };
  return internalSendDeleteEntry(parentLineItem, [subLineItemToDelete]);
}

async function internalSendDeleteEntry(entry: LineItem, subLineItems: SubLineItem[]): Promise<unknown> {
  if (userIsSalesman()) {
    const apiItem = QuoteUpdateApi.fromLineItem(entry);
    return QuoteUpdateApi.sendUpdate(apiItem);
  } else {
    const apiItem = CartUpdateApi.fromLineItem(entry, subLineItems);
    await CartDeleteApi.sendDelete(apiItem);
    return;
  }
}

async function sendEditEntry(entry: LineItem, subLineItems: SubLineItem[]): Promise<unknown> {
  if (userIsSalesman()) {
    const apiItem = QuoteUpdateApi.fromLineItem(entry);
    return QuoteUpdateApi.sendUpdate(apiItem);
  } else {
    const apiItem = CartUpdateApi.fromLineItem(entry, subLineItems);
    await CartUpdateApi.sendUpdate(apiItem);
    return;
  }
}

export function sendBulkEditEntry(items: LineItem[], subLineItemsByParentId: Dict<SubLineItem[]>): Promise<unknown> {
  if (userIsSalesman()) {
    const apiItems = items.map((item) => QuoteUpdateApi.fromLineItem(item));
    return QuoteUpdateApi.sendBulkUpdate(apiItems);
  } else {
    const apiItems = items.map((item) => CartUpdateApi.fromLineItem(item, subLineItemsByParentId[item.id]));
    return CartUpdateApi.sendBulkUpdate(apiItems);
  }
}

export async function sendAddEntry(
  item: LineItem,
  descriptor: ProductDescriptor,
  subLineItems: SubLineItem[],
): Promise<number> {
  let result;
  if (userIsSalesman()) {
    result = await QuoteAddApi.sendAddToQuote(QuoteAddApi.fromLineItem(item, descriptor));
    result = await result.json();
    return result[0] as number;
  } else {
    result = await CartAddApi.sendAddToCart(CartAddApi.fromLineItem(item, subLineItems, descriptor), true);
    result = await result.json();
    return result[0] as number;
  }
}

export function loadCart() {
  if (userIsSalesman()) {
    return QuoteLoadApi.getData();
  } else if (userHasPermission(Permission.ADD_TO_CART)) {
    return CartLoadApi.getData();
  }
  return Promise.resolve(null);
}
